import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../../store/auth-context';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { PreviewShell } from '../../preview/PreviewShell';
import {
    PreviewFooter,
    PreviewRunningHeader,
    type PreviewHeaderConfig,
    type PreviewMode,
} from '@nordinsight/implant-core/ui';
import { ImplantPreviewContent } from './ImplantPreviewContent';
import { buildImplantHeaderConfig } from './implantPreviewData';
import { buildImplantPlainText } from './implantPlainText';
import { getMriStatusText } from '../../reports/cpt/utils/cptFormUtils';
import { getApprovalInfo } from '../../../services/apiService';
import { getImplantNotes } from '../../../services/api/implantNoteService';
import type { RegionalInfo } from '../../../store/implant-context';
import Status from '../../general-components/Status';
import logoblack from '../../../images/Logoblack.png';

interface ImplantPreviewDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    info: any;
    regionalInfo: RegionalInfo;
    conditionals: any;
}

export function ImplantPreviewDialog({
    open,
    onOpenChange,
    info,
    regionalInfo,
    conditionals,
}: ImplantPreviewDialogProps) {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const [mode, setMode] = useState<PreviewMode>('print');
    const [approvalInfo, setApprovalInfo] = useState<any>(null);
    const [departmentNotes, setDepartmentNotes] = useState<any[]>([]);

    useEffect(() => {
        if (!open || !info?._id) return;
        let cancelled = false;
        getApprovalInfo(info._id).then((data) => {
            if (!cancelled) setApprovalInfo(data);
        });
        getImplantNotes(info._id)
            .then((data) => {
                if (!cancelled) setDepartmentNotes(data ?? []);
            })
            .catch(() => {
                if (!cancelled) setDepartmentNotes([]);
            });
        return () => {
            cancelled = true;
        };
    }, [open, info?._id]);

    const headerConfig = useMemo<PreviewHeaderConfig>(() => {
        const base = buildImplantHeaderConfig(
            {
                info,
                regionalInfo,
                conditionals,
                approvalInfo,
            },
            t
        );
        return {
            ...base,
            disclaimer: t('implant.preview.disclaimer'),
            logo: (
                <img
                    src={logoblack}
                    alt="NordInsight"
                    className="h-8 opacity-70"
                />
            ),
            statusBadge: (
                <div className="flex flex-col items-center [&_img]:h-12 [&_img]:w-auto">
                    <Status status={regionalInfo.mriStatus} small={false} />
                    <div className="mt-1.5 text-[9px] font-bold uppercase tracking-[0.15em]">
                        {getMriStatusText(regionalInfo.mriStatus)}
                    </div>
                </div>
            ),
        };
    }, [info, regionalInfo, conditionals, approvalInfo, t]);

    const plainText = useMemo(
        () =>
            buildImplantPlainText(
                {
                    info,
                    regionalInfo,
                    conditionals,
                    approvalInfo,
                    departmentNotes,
                },
                t
            ),
        [info, regionalInfo, conditionals, approvalInfo, departmentNotes, t]
    );

    const generateCopyText = useCallback(() => plainText, [plainText]);

    const printFooter = useMemo(() => {
        const facilityName = ctx.getContext()?.department?.name;
        const generatedAt = new Date().toLocaleDateString();
        return (
            <PreviewFooter
                left={['NordInsight', facilityName].filter(Boolean).join(' · ')}
                right={t('preview.printed', { date: generatedAt })}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, open, t]);

    const hasContent =
        !!info?.[info.Model || info.model] || !!regionalInfo?.model;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
                <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6 pr-14">
                    <DialogTitle>
                        {mode === 'print'
                            ? t('implant.preview.clinicalSummary')
                            : t('preview.shell.copyOverview')}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                        {mode === 'print'
                            ? t('preview.common.formatForPrintBranded')
                            : t('preview.common.plainTextSummary')}
                    </p>
                </DialogHeader>
                <PreviewShell
                    className="min-h-0 flex-1"
                    mode={mode}
                    onModeChange={setMode}
                    header={headerConfig}
                    hasContent={hasContent}
                    printHeader={
                        <PreviewRunningHeader
                            label={info?.model || regionalInfo?.model || ''}
                        />
                    }
                    printHeaderSkipFirstPage
                    printFooter={printFooter}
                    generateCopyText={generateCopyText}
                    showPrint={mode === 'print'}
                    showCopy={mode === 'copy'}
                    onClose={() => onOpenChange(false)}
                    previewSource="implant"
                >
                    <ImplantPreviewContent
                        info={info}
                        regionalInfo={regionalInfo}
                        conditionals={conditionals}
                        approvalInfo={approvalInfo}
                        departmentNotes={departmentNotes}
                        mode={mode}
                        plainText={plainText}
                    />
                </PreviewShell>
            </DialogContent>
        </Dialog>
    );
}
